import {
  type FC,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';
import { endOfDay, formatDate, isValid, parseISO } from 'date-fns';
import { isString, upperFirst } from 'lodash-es';

import {
  DATE_WITH_TIMEZONE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@iconik-io/core/src/date/date_formats';
import { format } from '@iconik-io/core/src/date/format';
import type { MetadataValue } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import {
  SEARCH_CUSTOM_FILTER_CONFIRMED_PERSONS,
  SEARCH_CUSTOM_FILTER_CREATED_BY,
  SEARCH_CUSTOM_FILTER_CREATED_BY_USER,
  SEARCH_CUSTOM_FILTER_DATE_CREATED,
  SEARCH_CUSTOM_FILTER_DATE_MODIFIED,
  SEARCH_CUSTOM_FILTER_METADATA_VALUES,
  SEARCH_CUSTOM_FILTER_STORED_IN,
  SEARCH_CUSTOM_FILTER_TRANSCRIPTION_TEXT,
} from '@iconik-io/shared-lib/src/constants/search';
import type {
  FacetValues,
  FilterValues,
  MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { guardMultiSelectFilter } from '@iconik-io/shared-lib/src/utils/search/guard_multi_select_filter';

import MetadataFormContainer from '../../components/metadata_form/metadata_form_container';
import metadataFieldSearchRenderer from '../../components/metadata_form_field/search';
import SearchFacets from '../../components/search_facets/search_facets';
import SearchFiltersSeparator from '../../components/search_filters_separator/search_filters_separator';
import {
  getMetadataUserFields,
  selectAvailableSearchFieldsMap,
} from '../../components/search_items/selectors';
import { archiveStatus } from '../../constants/archive';
import { assetTypes } from '../../constants/data';
import { searchReviewStatuses } from '../../constants/reviewapproval';
import {
  COLLECTION_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
} from '../../constants/routes';
import { getMetadataCategoryViews } from '../../ducks/pages/admin_metadata_categories/thunks';
import { selectEffectiveMetadataViewId } from '../../ducks/panels/search_filters/metadata/selectors';
import { setSearchFiltersMetadataViewId } from '../../ducks/panels/search_filters/metadata/slice';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import type { FacetSchema, FacetsSchema } from '../../types/common';
import DateFieldWrapper from '../../ui/forms/field_wrappers/date/date';
import { MultiSelectFilterPersonField } from '../../ui/forms/field_wrappers/multi_select_filter_person_field/multi_select_filter_person_field';
import { MultiSelectFilterStorageField } from '../../ui/forms/field_wrappers/multi_select_filter_storage_field/multi_select_filter_storage_field';
import { MultiSelectFilterTextField } from '../../ui/forms/field_wrappers/multi_select_filter_text_field/multi_select_filter_text_field';
import { MultiSelectFilterUserField } from '../../ui/forms/field_wrappers/multi_select_filter_user_field/multi_select_filter_user_field';
import SelectMetadataViewFieldWrapper from '../../ui/forms/field_wrappers/select_metadata_view/select_metadata_view';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import Form from '../../ui/forms/form';
import type { FormFieldEvent } from '../../ui/forms/types';
import { nestedObjectToDotNotation } from '../../ui/forms/utils/nested_object_to_dot_notation';
import { facetsCleanup } from '../../utils/search_utils/facets_cleanup';
import {
  guardFilterGeneric,
  guardFilterRange,
  guardFilterString,
} from '../../utils/search_utils/guard_filter';
import { getStorageAvailableModifiers } from '../../utils/search_utils/storage_modifiers';
import SearchFilterElements from './search_filter_elements';
import { getVisibleMetadataFieldNames } from './visible_metadata_fields';

import styles from './search_filters.module.css';

const labels: Record<string, string> = {};

assetTypes.forEach((v) => {
  labels[v.value] = v.label;
});

searchReviewStatuses.forEach((v) => {
  labels[v.value] = v.label;
});

archiveStatus.forEach((v) => {
  labels[v.value] = v.label;
});

type FiltersForm = {
  facets_filters?: FacetValues;
  filters?: FilterValues;
  metadataViewId?: string;
};

// Metadata fields render under this prefix and are the only ones that mount and
// unmount as the selected view changes; other filter fields keep their model
// value when they unmount so their (still active) Redux filter isn't dropped.
const isMetadataFormField = (name: string): boolean =>
  name.startsWith('filters.metadata.');

type Props = {
  onFilterSearch: (data: FiltersForm) => void;
  dateFormat: string;
  facetValues: FacetValues;
  filterValues: FilterValues;
  canReadMetadataViews: boolean;
  availableFacets?: FacetsSchema;
  searchQuery?: string;
  onSearchString?: (query: string | undefined, event?: FormFieldEvent) => void;
};

const SearchFiltersPanel: FC<Props> = (props) => {
  const route = useAppSelector((state) => state.routeParams.route);
  const metadataFields = useAppSelector(getMetadataUserFields);
  const metadataViewId = useAppSelector(selectEffectiveMetadataViewId);
  const metadataViews = useAppSelector(
    (state) => state.entities.metadata_views,
  );
  const metadataEntityFields = useAppSelector(
    (state) => state.entities.metadata_fields,
  );
  const hasMetadataFacet = useAppSelector((state) =>
    (state.settings.merged.facet_fields ?? []).some(
      (facet) => facet.name === SEARCH_CUSTOM_FILTER_METADATA_VALUES,
    ),
  );

  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const dispatch = useAppDispatch();

  const handleMetadataViewChange = useCallback(
    (viewId: string | undefined): void => {
      dispatch(setSearchFiltersMetadataViewId(viewId));
    },
    [],
  );

  useEffect(() => {
    setLoadingMetadata(true);
    dispatch(getMetadataCategoryViews('search', 'default')).finally(() => {
      setLoadingMetadata(false);
    });
  }, []);

  const {
    onSearchString,
    searchQuery,
    filterValues,
    facetValues,
    onFilterSearch,
    canReadMetadataViews,
    dateFormat,
  } = props;

  // Metadata field names currently mounted in the panel. Filters for fields not
  // in this set (other views, or all metadata fields when the form isn't shown
  // or is still loading) are merged back in onChange since the form can't carry
  // them.
  const visibleMetadataFieldNames = getVisibleMetadataFieldNames({
    metadataFormRendered: canReadMetadataViews && hasMetadataFacet,
    metadataViewId,
    loadingMetadata,
    metadataViews,
    metadataFields: metadataEntityFields,
    filterValues,
  });

  // Labels for facets that represent drop down fields need to be mapped based on the options
  // This is because we get the value for the option in the buckets but we want to display the label
  const facetLabelFormatter = useCallback(
    (facetKey: string, bucketKey: string): string => {
      if (facetKey.startsWith('metadata.')) {
        const fieldName = facetKey.replace('metadata.', '');
        const field = metadataFields[fieldName];
        if (field && field.field_type === 'drop_down') {
          const option = field.options?.find(
            (item) => item.value === bucketKey,
          );
          if (option?.label) {
            return option.label;
          }
        }

        if (field && field.field_type === 'date') {
          return formatDate(bucketKey, dateFormat);
        }
        return bucketKey;
      }
      return labels[bucketKey] ?? upperFirst(bucketKey);
    },
    [metadataFields, dateFormat],
  );

  const availableFacets = facetsCleanup(props.availableFacets ?? {});
  const creationDate = guardFilterRange(filterValues.date_created);
  const modifiedDate = guardFilterRange(filterValues.date_modified);
  const filterCollectionAncestors = filterValues.ancestor_collections;
  const filterInCollections = filterValues.in_collections;
  const createdByUser = guardMultiSelectFilter(filterValues.created_by_user);
  const newAssetIds = guardFilterGeneric(filterValues.id);
  const storedIn = guardMultiSelectFilter(filterValues.storage_id);
  const transcriptionTextValue = guardMultiSelectFilter(
    filterValues.transcription_text,
  );
  const personsFilterValue = guardMultiSelectFilter(filterValues.person_ids);
  const storageAllowedModifiers = getStorageAvailableModifiers(
    filterValues.storage_id,
    facetValues.object_type,
  );

  const metadataValues: Record<string, MetadataValue | MultiSelectFilterValue> =
    {};

  for (const [name, value] of Object.entries(filterValues)) {
    if (name.startsWith('metadata.')) {
      metadataValues[`filters.${name}`] =
        guardMultiSelectFilter(value, { convertLegacyStrings: false }) ??
        guardFilterString(value) ??
        guardFilterGeneric(value) ??
        guardFilterRange(value);
    }
  }

  const availableFacetsConfigured: Record<string, FacetSchema> = {};

  const availableFieldsMap = useAppSelector(selectAvailableSearchFieldsMap);
  const mergedSettings = useAppSelector((state) => state.settings.merged);
  const facets = mergedSettings.facet_fields ?? [];
  for (const facet of facets) {
    const realField = availableFieldsMap[facet.name];
    const availableFacet = availableFacets[facet.name];
    if (!availableFacet && realField && facetValues[facet.name]?.length) {
      // Buckets only arrive with a regular search list response, so a facet
      // selection can exist before (or without) one — e.g. filters derived by
      // an AI search, which never fetches the list the buckets come from.
      // `SearchFacets` prepends selected values missing from the buckets, so an
      // empty bucket list is enough to get the group rendered.
      availableFacetsConfigured[facet.name] = {
        facetLabel: realField.label,
        buckets: [],
      };
      continue;
    }
    if (availableFacet && realField) {
      if (
        realField.name.includes('metadata') &&
        Array.isArray(availableFacet.buckets) &&
        realField.type === 'date'
      ) {
        const buckets = availableFacet.buckets.map((item) => {
          const date = new Date(item.key).toISOString();
          const key = formatDate(date, DEFAULT_DATE_FORMAT);

          const value = {
            ...item,
            key,
          };

          return value;
        });
        availableFacetsConfigured[facet.name] = {
          ...availableFacet,
          facetLabel: realField.label,
          buckets,
        };
      } else {
        availableFacetsConfigured[facet.name] = {
          facetLabel: realField.label,
          ...availableFacet,
        };
      }
    }
  }

  const canReadPerson = useAppSelector(
    (state) => state.rolesReducer.userRoles.canReadPerson,
  );

  const dateCreatedValue = Array.isArray(creationDate)
    ? creationDate[0]
    : creationDate;
  const dateModifiedValue = Array.isArray(modifiedDate)
    ? modifiedDate[0]
    : modifiedDate;

  const elements: ReactElement[] = [];

  const hasFixedElements =
    Boolean(newAssetIds) || route === COLLECTION_PAGE_ROUTE;
  if (hasFixedElements) {
    elements.push(
      <SearchFilterElements
        collectionAncestors={Boolean(filterCollectionAncestors)}
        newAssetIds={newAssetIds}
        key="filters"
      />,
      <SearchFiltersSeparator key="filters-separator" />,
    );
  }

  let createdByRendered = false;
  for (const facet of facets) {
    let element: ReactElement | null = null;

    switch (facet.name) {
      case SEARCH_CUSTOM_FILTER_DATE_CREATED:
        element = (
          <div className={styles.filter_content} key={facet.name}>
            <DateFieldWrapper
              dateFormat={dateFormat}
              name="filters.date_created"
              value={dateCreatedValue}
              vertical
              label="Date Created"
            />
          </div>
        );
        break;
      case SEARCH_CUSTOM_FILTER_DATE_MODIFIED:
        element = (
          <div className={styles.filter_content} key={facet.name}>
            <DateFieldWrapper
              dateFormat={dateFormat}
              name="filters.date_modified"
              value={dateModifiedValue}
              vertical
              label="Date Modified"
            />
          </div>
        );
        break;
      case SEARCH_CUSTOM_FILTER_CREATED_BY:
      case SEARCH_CUSTOM_FILTER_CREATED_BY_USER:
        if (!createdByRendered) {
          createdByRendered = true;
          element = (
            <div className={styles.filter_content} key={facet.name}>
              <MultiSelectFilterUserField
                name="filters.created_by_user"
                value={createdByUser}
                vertical
                label="Created by"
                subtitle="Items must..."
                maxRows={1}
              />
            </div>
          );
        }
        break;
      case SEARCH_CUSTOM_FILTER_STORED_IN:
        element = (
          <div className={styles.filter_content} key={facet.name}>
            <MultiSelectFilterStorageField
              name="filters.storage_id"
              includeCollectionsTerm
              availableModifiers={storageAllowedModifiers}
              params={{
                purpose: ['FILES', 'ARCHIVE', 'KEYFRAMES', 'PROXIES'],
              }}
              value={storedIn}
              vertical
              subtitle="Items must..."
              label="Storage"
            />
          </div>
        );
        break;
      case SEARCH_CUSTOM_FILTER_CONFIRMED_PERSONS:
        if (canReadPerson) {
          element = (
            <div className={styles.filter_content} key={facet.name}>
              <MultiSelectFilterPersonField
                name="filters.person_ids"
                vertical
                label="Confirmed Persons"
                subtitle="Items must..."
                value={personsFilterValue}
              />
            </div>
          );
        }
        break;
      case SEARCH_CUSTOM_FILTER_TRANSCRIPTION_TEXT:
        element = (
          <div className={styles.filter_content} key={facet.name}>
            <MultiSelectFilterTextField
              name="filters.transcription_text"
              value={transcriptionTextValue}
              vertical
              label="Transcription Text"
              subtitle="Items must..."
            />
          </div>
        );
        break;
      case SEARCH_CUSTOM_FILTER_METADATA_VALUES:
        if (canReadMetadataViews) {
          element = (
            <div
              className={classNames({
                [styles.metadata_filters_expanded]: Boolean(metadataViewId),
              })}
              key={facet.name}
            >
              <SelectMetadataViewFieldWrapper
                isMulti={false}
                objectType="search"
                category="default"
                onChange={handleMetadataViewChange}
                value={metadataViewId ?? undefined}
                label="Metadata View"
                filterValuesForIndicator={filterValues}
                vertical
              />
              <MetadataFormContainer
                displayFieldsIfNoView
                componentClass="div"
                prefix="filters.metadata."
                metadataValues={metadataValues}
                loading={Boolean(loadingMetadata && metadataViewId)}
                vertical
                fieldRenderer={metadataFieldSearchRenderer}
                metadataViewId={metadataViewId ?? undefined}
              />
            </div>
          );
        }
        break;
      default: {
        const facetData = availableFacetsConfigured[facet.name];
        const hasFacetContent =
          facetData &&
          ((facetData.buckets?.length ?? 0) > 0 ||
            (facetValues[facet.name]?.length ?? 0) > 0);
        if (hasFacetContent) {
          element = (
            <SearchFacets
              availableFacets={{ [facet.name]: facetData }}
              facetValues={facetValues}
              labelFormatter={facetLabelFormatter}
              key={facet.name}
            />
          );
        }
        break;
      }
    }

    if (element) {
      elements.push(
        element,
        <SearchFiltersSeparator key={`${facet.name}-sep`} />,
      );
    }
  }

  // remove the last separator
  if (elements.length > 0) {
    elements.pop();
  }

  return (
    <div className={styles.wrapper}>
      {onSearchString ? (
        <Form className={styles.search_field}>
          <TextFieldWrapper
            name="query"
            inputMode="search"
            value={searchQuery}
            onChange={onSearchString}
            label="Search"
            vertical
          />
          {elements.length > 0 ? <SearchFiltersSeparator /> : undefined}
        </Form>
      ) : undefined}
      <Form<FiltersForm>
        className={styles.form}
        unregisterFieldOnUnmount={isMetadataFormField}
        onChange={(formData) => {
          const formFacetValues: FacetValues = {};

          const { metadata: metadataFacetFilters = {}, ...restFacetFilters } =
            formData.facets_filters ?? {};
          const dotNotationRestFacetFilters =
            nestedObjectToDotNotation(restFacetFilters);

          // drop empty facet filters
          for (const [name, value] of Object.entries(
            dotNotationRestFacetFilters,
          )) {
            if (value.length > 0) {
              formFacetValues[name] = value;
            }
          }

          // The form nests metadata facets under their own key, so this entry
          // is a FacetValues rather than the string[] FacetValues promises for
          // every value. Fixing that means retyping the form and the grid
          // panel that shares it.
          for (const [metadataName, metadataFilter] of Object.entries(
            // biome-ignore lint/plugin: see above.
            metadataFacetFilters as FacetValues,
          )) {
            formFacetValues[`metadata.${metadataName}`] = metadataFilter;
          }

          const formFilterValues: FilterValues = {};
          const {
            date_created: dateCreatedFilter,
            storage_id: storageIdFilter,
            metadata: metadataFilters = {},
            ...restFilters
          } = formData.filters ?? {};

          formFilterValues.storage_id = storageIdFilter;

          for (const [metadataName, metadataFilter] of Object.entries(
            metadataFilters,
          )) {
            if (metadataFilter === '') {
              continue;
            }
            formFilterValues[`metadata.${metadataName}`] = metadataFilter;
          }

          // Carry over metadata filters from other views: their inputs aren't
          // mounted, so the form can't report them and the replace downstream
          // would drop them. Current-view fields come from the form above.
          for (const [key, value] of Object.entries(filterValues)) {
            if (!key.startsWith('metadata.')) {
              continue;
            }
            const fieldName = key.slice('metadata.'.length);
            if (visibleMetadataFieldNames.has(fieldName)) {
              continue;
            }
            formFilterValues[key] = value;
          }

          // if date_created is not a range, convert it to a range with
          // the full current day selected
          if (
            isString(dateCreatedFilter) &&
            dateCreatedFilter.length > 0 &&
            isValid(new Date(dateCreatedFilter))
          ) {
            formFilterValues.date_created = {
              min: dateCreatedFilter,
              max:
                format(
                  endOfDay(parseISO(dateCreatedFilter)),
                  DATE_WITH_TIMEZONE_FORMAT,
                ) ?? undefined,
            };
          } else {
            formFilterValues.date_created = dateCreatedFilter;
          }

          const filters = {
            ...restFilters,
            ...formFilterValues,
          };

          if (route === SAVED_SEARCH_PAGE_ROUTE) {
            // It is not possible to create search criteria that have both ancestor_collections
            // and in_collections, but it can be possible via API. In this case we decided to keep
            // them both, because AND operator in Elastic will perform the correct filtering
            if (filterCollectionAncestors) {
              filters.ancestor_collections = filterCollectionAncestors;
            }
            if (filterInCollections) {
              filters.in_collections = filterInCollections;
            }
          }

          const nonEmptyFilters = Object.fromEntries(
            Object.entries(filters).filter(([, value]) =>
              Array.isArray(value) ? value.length > 0 : Boolean(value),
            ),
          );
          const resultFilters = {
            facets_filters: formFacetValues,
            filters: nonEmptyFilters,
          };
          onFilterSearch(resultFilters);
        }}
      >
        {elements}
      </Form>
    </div>
  );
};

export default SearchFiltersPanel;
